import React, { useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { HardHat, Loader2, Mail, ArrowLeft, CheckCircle2 } from "lucide-react";

export default function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const { data } = await api.post("/auth/forgot-password", { email: email.trim() });
      setMessage(data.message);
      setSent(true);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-slate-50 p-6 dark:bg-slate-950">
      <div className="w-full max-w-md">
        <div className="mb-8 flex items-center gap-3">
          <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-amber-500">
            <HardHat className="h-6 w-6 text-slate-900" />
          </div>
          <span className="font-head text-xl font-extrabold">IR INGENIERIE</span>
        </div>

        {sent ? (
          <div className="rounded-2xl border border-emerald-200 bg-emerald-50 p-6 dark:border-emerald-900 dark:bg-emerald-950/40" data-testid="forgot-success">
            <CheckCircle2 className="h-8 w-8 text-emerald-600" />
            <h2 className="mt-3 font-head text-2xl font-bold text-slate-900 dark:text-white">Email envoyé</h2>
            <p className="mt-2 text-sm text-slate-600 dark:text-slate-300">{message}</p>
            <p className="mt-2 text-sm text-slate-500">Pensez à vérifier votre dossier spam. Le lien est valable 1 heure.</p>
            <Link to="/login">
              <Button className="mt-5 h-11 w-full gap-2 bg-slate-900 text-white dark:bg-white dark:text-slate-900" data-testid="back-to-login-btn">
                <ArrowLeft className="h-4 w-4" /> Retour à la connexion
              </Button>
            </Link>
          </div>
        ) : (
          <>
            <h2 className="font-head text-3xl font-bold tracking-tight text-slate-900 dark:text-white">Mot de passe oublié</h2>
            <p className="mt-2 text-sm text-slate-500">Saisissez votre email : nous vous enverrons un lien pour réinitialiser votre mot de passe.</p>
            <form onSubmit={submit} className="mt-8 space-y-4">
              <label className="block">
                <span className="mb-1.5 block text-xs font-semibold text-slate-500">Email</span>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                  <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
                    data-testid="forgot-email" placeholder="vous@entreprise.com" name="email" autoComplete="username"
                    className="h-12 w-full rounded-lg border border-slate-300 bg-white pl-10 pr-3 text-sm outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 dark:border-slate-700 dark:bg-slate-900" />
                </div>
              </label>
              {error && (
                <div data-testid="forgot-error" className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700 dark:border-red-900 dark:bg-red-950/50 dark:text-red-300">{error}</div>
              )}
              <Button type="submit" disabled={loading} data-testid="forgot-submit"
                className="h-12 w-full bg-amber-500 text-base font-semibold text-slate-900 hover:bg-amber-400">
                {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : "Envoyer le lien"}
              </Button>
              <Link to="/login" className="flex items-center justify-center gap-1.5 text-sm font-medium text-slate-500 hover:text-slate-800 dark:hover:text-slate-200" data-testid="back-login-link">
                <ArrowLeft className="h-4 w-4" /> Retour à la connexion
              </Link>
            </form>
          </>
        )}
      </div>
    </div>
  );
}
